import React, { useState, useEffect } from "react";
import CustomTable from "./common/table/CustomTable";
import { IField } from "../types/table/interfaces.type";
import {
  ExportErrorApi,
  IErrors,
  useImportErrorsQuery,
} from "../services/ExportService";
import { COLORS } from "../assets/theme";
import { Box, useMediaQuery, useTheme } from "@mui/material";
import CustomButton from "./common/CustomButton";
import { exportMessageTracker } from "../services/MessageTrackerService";
import { ReactComponent as Export } from "../assets/icons/export.svg";
import SearchHeaderContainer from "./common/table/SearchHeaderContainer";

interface GetErrorsProps {
  fields: IField<IErrors>[];
  exportId?: string;
  usedBy: string;
}

const GetErrors: React.FC<GetErrorsProps> = ({ fields, exportId, usedBy }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const exportPermissions: Record<string, string> = {
    OPERATOR: "GET_ALL_OPERATOR_IMPORT_ERRORS_CSV",
    PREFIX: "GET_ALL_OPERATOR_PREFIX_IMPORT_ERRORS_CSV",
    COST_PLAN: "GET_ALL_COST_PLAN_IMPORT_ERRORS_CSV",
    RATE: "GET_ALL_RATE_ROUTE_IMPORT_ERRORS_CSV",
    SERVICE_PROVIDER_COST: "GET_ALL_SERVICE_PROVIDER_COST_IMPORT_ERRORS_CSV",
  };
  const exportPermission = exportPermissions[usedBy] ?? "";
  
  const [appliedFilters, setAppliedFilters] = useState<any>({
    importedFileId: exportId,
    search: "",
  });

  useEffect(() => {
    setAppliedFilters((prev: any) => ({
      importedFileId: exportId,
      search: prev.search,
    }));
  }, [exportId]);

  const handleSearchChange = (value: string) => {
    setAppliedFilters((prev: any) => ({
      ...prev,
      search: value,
    }));
  };

  return (
    <Box 
      sx={{
        width: "100%",
        maxWidth: "100%",
        overflow: "hidden"
      }}
    >
      <Box 
        sx={{
          width: "100%", 
          alignItems: "center", 
          mb: { xs: 1, sm: 2 },
          px: { xs: 1, sm: 0 }
        }}
      >
        <Box 
          sx={{
            display: "flex", 
            justifyContent: { xs: "center", sm: "flex-end" }, 
            mb: { xs: 1, sm: 2 },
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 }
          }}
        >
          <SearchHeaderContainer
            search={appliedFilters.search ?? ""}
            setSearch={handleSearchChange}
            buttonConfig={[
              {
                size: isMobile ? "small" : "medium",
                bgColor: COLORS.GOLD,
                startIcon: <Export />,
                onClick: async () => {
                  try {
                    await ExportErrorApi(appliedFilters.importedFileId);
                  } catch (error) {
                    console.error("Export failed:", error);
                  }
                },
                showExportProgress: true,
                textColor: COLORS.iconColor,
                label: "Export",
                permission: exportPermission,
              },
            ]}
          />
        </Box>
        <Box
          sx={{
            overflowX: "auto",
            width: "100%",
            "& .MuiTableContainer-root": {
              maxHeight: { xs: "60vh", sm: "70vh" },
              overflowY: "auto"
            }
          }}
        >
          <CustomTable<IErrors> fullBleedMobile={false}
            filters={appliedFilters}
            fields={fields}
            fetchFunc={useImportErrorsQuery}
            sortableFields={["errorMessage", "createdDate", "createdBy"]}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default GetErrors;
